// src/lib/coachLinks.js
// STATUS: active | UPDATED: 2026-09-12
// LOG:
//   2026-09-12  New. The coach can send somebody to a page, not describe one.
// Turning what the coach says into somewhere you can tap.
//
// TOKENS, NOT URLS, AND THAT IS THE WHOLE SAFETY DESIGN.
//
// The coach never emits a link. It emits a token naming a THING, and this file
// resolves it against the real route table. Three reasons that ordering matters:
//
// 1. It cannot invent a broken link. A model asked for a URL will cheerfully
//    produce /courses/step-6/ because that is what a URL to step six should
//    look like. A step number either resolves against STEPS or renders as
//    nothing at all.
// 2. It cannot emit a link off this site. There is no free text in the output
//    of this function, so there is no phishing surface and nothing to sanitise.
// 3. Routes can move. When they do, this file changes and every sentence the
//    coach has ever written keeps working.
//
// Unresolvable tokens are DROPPED rather than shown. A dead button in a
// coaching reply is worse than a sentence that reads slightly plainly.

// Tabs on a step page, by the name a person would say out loud.
const TABS = { learn: 0, words: 1, practice: 2, check: 3 }

const FIXED = {
  roadmap: { to: '/courses/roadmap/', label: 'Open the roadmap' },
  dashboard: { to: '/dashboard/', label: 'Go to today' },
  team: { to: '/team/', label: 'Open your team' },
  reference: { to: '/courses/reference/', label: 'Open the reference' },
  settings: { to: '/settings/', label: 'Open settings' },
}

const TAB_LABEL = {
  check: 'Take the check on Step',
  practice: 'Practise Step',
  words: 'See the words for Step',
  learn: 'Read Step',
}

// [[step:6]] [[step:6:check]] [[roadmap]] and so on.
// The middle group excludes ':' deliberately. Greedy, it swallowed "6:check"
// whole, Number() gave NaN, and every tab token silently resolved to nothing.
const TOKEN = /\[\[([a-z]+)(?::([^\]|:]+))?(?::([a-z]+))?\]\]/gi

function resolve(kind, a, b, steps) {
  const k = String(kind || '').toLowerCase()
  if (FIXED[k]) return FIXED[k]
  if (k !== 'step') return null

  const n = Number(a)
  const step = steps.find((s) => s.number === n)
  // The guard that makes this safe. A hallucinated step 14 resolves to nothing
  // and the token is dropped.
  if (!step) return null

  const tab = String(b || '').toLowerCase()
  const to = TABS[tab] !== undefined ? `/courses/roadmap/${step.slug}/?tab=${tab}` : `/courses/roadmap/${step.slug}/`
  const head = TAB_LABEL[tab] || 'Open Step'
  return { to, label: `${head} ${step.number}, ${step.title}` }
}

// Splits a reply into text runs and resolved links, in order, so the caller can
// render one Text and one Button at a time without reassembling anything.
// `steps` is passed in rather than imported. This file deliberately pulls in
// nothing from the content bundle, so the resolver can be tested on its own and
// the only source of truth for what exists stays with the caller.
export function parseCoachLinks(content, steps = []) {
  const text = String(content || '')
  const parts = []
  let last = 0
  let m
  TOKEN.lastIndex = 0
  while ((m = TOKEN.exec(text)) !== null) {
    const link = resolve(m[1], m[2], m[3], steps)
    if (m.index > last) parts.push({ type: 'text', value: text.slice(last, m.index) })
    if (link) parts.push({ type: 'link', ...link })
    last = m.index + m[0].length
  }
  if (last < text.length) parts.push({ type: 'text', value: text.slice(last) })
  // Collapse the gap a dropped token leaves behind, so "go to  and tap" does
  // not ship with two spaces in it.
  return parts
    .map((p) => (p.type === 'text' ? { ...p, value: p.value.replace(/[ \t]{2,}/g, ' ') } : p))
    .filter((p) => p.type !== 'text' || p.value.length > 0)
}

export function hasCoachLinks(content, steps = []) {
  return parseCoachLinks(content, steps).some((p) => p.type === 'link')
}
